import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';

export const M2_DEFAULT = {
  volumetric_divisor: 5000, weight_ranges: [], over_max_per_kg: 0,
  oversize: { max_side_cm: null, extra: 0 }, origin_factors: { same_city: 1, same_department: 1, national: 1 },
  source: 'own', carrier_markup_percent: 0, carrier_markup_fixed: 0,
};

const n = (v) => (v === '' || v == null ? 0 : Number(v));
export const m2Payload = (p) => ({
  volumetric_divisor: n(p.volumetric_divisor) || 5000,
  weight_ranges: (p.weight_ranges || []).filter(r => r.max_kg !== '' && r.max_kg != null).map(r => ({ max_kg: Number(r.max_kg), extra: n(r.extra) })),
  over_max_per_kg: n(p.over_max_per_kg),
  oversize: { max_side_cm: p.oversize?.max_side_cm === '' || p.oversize?.max_side_cm == null ? null : Number(p.oversize.max_side_cm), extra: n(p.oversize?.extra) },
  origin_factors: Object.fromEntries(['same_city', 'same_department', 'national'].map(k => [k, n(p.origin_factors?.[k]) || 1])),
  source: p.source || 'own', carrier_markup_percent: n(p.carrier_markup_percent), carrier_markup_fixed: n(p.carrier_markup_fixed),
});

const Field = ({ label, value, onChange, testid, step }) => (
  <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">{label}
    <Input type="number" step={step} className="rounded-none h-8 w-44" value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={testid} />
  </label>
);

// Motor de tarifas de la Modalidad 2: base del destino × factor de origen + rango de peso + exceso + sobredimensión.
export const Mode2PricingEditor = ({ value, onChange }) => {
  const p = { ...M2_DEFAULT, ...value };
  const set = (patch) => onChange({ ...p, ...patch });
  const ranges = p.weight_ranges || [];
  const setRange = (i, k, v) => set({ weight_ranges: ranges.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  return (
    <div className="space-y-5" data-testid="m2-pricing-editor">
      <div className="border border-slate-200 bg-slate-50 p-3" data-testid="m2-source">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Fuente del precio al comprador</div>
        {[['own', 'Tarifas propias de AutoMarket', 'Tabla por destino, factores de origen y rangos de peso de abajo.'],
          ['servientrega', 'Servientrega vía MiPaquete + ajuste AutoMarket', 'Se consulta en tiempo real solo el precio de Servientrega, se suma el ajuste y se redondea hacia arriba a $100. El comprador solo ve el valor final. Si no hay respuesta o cobertura, se usan las tarifas propias.']].map(([v, l, d]) => (
          <label key={v} className="mb-1 flex items-start gap-2 text-sm">
            <input type="radio" checked={p.source === v} onChange={() => set({ source: v })} data-testid={`m2-source-${v}`} />
            <span><b>{l}</b><span className="block text-xs text-slate-500">{d}</span></span>
          </label>
        ))}
        {p.source === 'servientrega' && (
          <div className="mt-2 flex flex-wrap gap-3">
            <Field label="Ajuste porcentual (%)" step="0.5" value={p.carrier_markup_percent} testid="m2-markup-percent" onChange={v => set({ carrier_markup_percent: v })} />
            <Field label="Ajuste fijo por envío (COP)" value={p.carrier_markup_fixed} testid="m2-markup-fixed" onChange={v => set({ carrier_markup_fixed: v })} />
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500">Tarifa al comprador = tarifa estándar del destino × factor de origen + adicional del rango de peso + exceso por kg + adicional por tamaño. Peso cobrable = mayor entre peso real y volumétrico (largo × ancho × alto ÷ divisor), sumado por unidades. Valores vacíos o en 0 = sin recargo; factor 1 = sin ajuste.</p>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Factor según origen del vendedor</div>
        <div className="flex flex-wrap gap-3">
          {[['same_city', 'Misma ciudad'], ['same_department', 'Mismo departamento'], ['national', 'Nacional']].map(([k, l]) => (
            <Field key={k} label={l} step="0.05" value={p.origin_factors[k]} testid={`m2-factor-${k}`}
              onChange={v => set({ origin_factors: { ...p.origin_factors, [k]: v } })} />
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Rangos de peso cobrable</div>
        <div className="mb-1 flex gap-2 text-xs font-semibold text-slate-700"><span className="w-36">Hasta (kg)</span><span className="w-36">Adicional (COP)</span></div>
        {ranges.map((r, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <Input type="number" min="0" className="rounded-none h-8 w-36" value={r.max_kg ?? ''} onChange={e => setRange(i, 'max_kg', e.target.value)} data-testid={`m2-range-max-${i}`} />
            <Input type="number" min="0" className="rounded-none h-8 w-36" value={r.extra ?? ''} onChange={e => setRange(i, 'extra', e.target.value)} data-testid={`m2-range-extra-${i}`} />
            <button onClick={() => set({ weight_ranges: ranges.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-rose-600" data-testid={`m2-range-del-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => set({ weight_ranges: [...ranges, { max_kg: '', extra: '' }] })} data-testid="m2-range-add">
          <Plus className="h-3 w-3 mr-1" /> Agregar rango
        </Button>
        <div className="mt-3"><Field label="Por cada kg sobre el último rango (COP)" value={p.over_max_per_kg} testid="m2-over-per-kg" onChange={v => set({ over_max_per_kg: v })} /></div>
      </div>
      <div className="flex flex-wrap gap-3">
        <Field label="Divisor volumétrico" value={p.volumetric_divisor} testid="m2-divisor" onChange={v => set({ volumetric_divisor: v })} />
        <Field label="Lado máximo sin recargo (cm)" value={p.oversize?.max_side_cm} testid="m2-oversize-side" onChange={v => set({ oversize: { ...p.oversize, max_side_cm: v } })} />
        <Field label="Adicional por tamaño (COP)" value={p.oversize?.extra} testid="m2-oversize-extra" onChange={v => set({ oversize: { ...p.oversize, extra: v } })} />
      </div>
    </div>
  );
};
